import { Icon, Divider, Loader, Message, Button, Dimmer } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import ConfirmModal from '../common/ConfirmModal';
import PersonImageEditor from '../person-image-editor/PersonImageEditor';
import DeletionWarningModal from '../deletion-warning-modal/DeletionWarningModal';
import ProcessingModal from '../common/ProcessingModal';

// Local components
import ActionButtons from './ActionButtons';
import SelectedImageControls from './SelectedImageControls';
import ImageHistoryGrid from './ImageHistoryGrid';
import DeleteConfirmModal from './DeleteConfirmModal';
import usePersonImageModalHandlers from './usePersonImageModalHandlers';

import './PersonImageModal.css';

/**
 * PersonImageModal - Modal for managing person/wearer images
 * 
 * Features:
 * - Upload photos from device or camera
 * - View and select from recent images
 * - Edit image name, set as default
 * - Crop/resize images
 * - Remove/toggle background
 * - Delete images with cascade warning for VTO results
 * - Bulk delete with confirmation
 */
const PersonImageModal = ({
  isOpen,
  onClose
}) => {
  const {
    // Refs
    fileInputRef,

    // State
    deleteConfirmOpen,
    imageToDelete,
    deleteError,
    bgRemovalError,
    setBgRemovalError,
    editingName,
    tempName,
    setTempName,
    defaultError,
    setDefaultError,
    nameError,
    setNameError,
    isEditorOpen,
    setIsEditorOpen,
    editorImageUrl,
    isSavingCrop,
    showDeletionWarning,
    vtoCount,
    deletionError,
    showBgRemovalProcessing,

    // Computed
    isOperationInProgress,
    hasPersonImage,
    isDeletingWithCascade,
    isLoadingImages,

    // Data
    personImages,
    activePersonImage,
    activePersonImageId,
    isCanvasIntegrated,
    creditBalance,
    hasCredits,
    daysUntilReset,

    // Bulk delete state
    bulkDeleteIds,
    bulkVtoCount,
    showBulkDeleteConfirm,
    showBulkDeletionWarning,
    bulkDeletionError,
    isBulkDeleting,

    // Handlers
    handleFileInputClick,
    handleLocalFileUpload,
    handleTakePicture,
    handleRemovePersonImage,
    handleRemoveBackgroundClick,
    handleToggleBackground,
    handleSelectPersonImage,
    handleConfirmDelete,
    handleCancelDelete,
    handleCancelDeletionWarning,
    handleConfirmCascadeDelete,
    handleSetDefault,
    handleSaveName,
    handleStartEditName,
    handleOpenEditor,
    handleEditorSave,
    handleDeleteClick,
    handleBulkDeleteClick,
    handleConfirmBulkDelete,
    handleConfirmBulkCascadeDelete,
    handleCancelBulkDelete
  } = usePersonImageModalHandlers({ onClose });

  return (
    <>
      <SusuModal
        open={isOpen}
        onClose={onClose}
        className="person-image-modal"
        size="small"
        closeIcon
      >
        {isOperationInProgress && (
          <div className="modal-loading-overlay">
            <Loader active size="large" />
          </div>
        )}

        <SusuModal.Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="male" style={{ margin: 0 }} />
            <span>Wearer Management</span>
          </div>
        </SusuModal.Header>

        <SusuModal.Content scrolling>
          <div className="person-modal-content">
            {/* Action Buttons */}
            <ActionButtons
              fileInputRef={fileInputRef}
              onFileInputClick={handleFileInputClick}
              onTakePicture={handleTakePicture}
              onLocalFileUpload={handleLocalFileUpload}
              isOperationInProgress={isOperationInProgress}
            />

            {/* Error Messages */}
            {bgRemovalError && (
              <Message negative onDismiss={() => setBgRemovalError(null)}>
                <Message.Header>Background Removal Failed</Message.Header>
                <p>{bgRemovalError}</p>
              </Message>
            )}

            {defaultError && (
              <Message negative onDismiss={() => setDefaultError(null)}>
                <Message.Header>Set Default Failed</Message.Header>
                <p>{defaultError}</p>
              </Message>
            )}

            {nameError && (
              <Message negative onDismiss={() => setNameError(null)}>
                <Message.Header>Update Name Failed</Message.Header>
                <p>{nameError}</p>
              </Message>
            )}

            {hasPersonImage && (
              <>
                <Divider />

                {/* Selected Image Controls */}
                <SelectedImageControls
                  activePersonImage={activePersonImage}
                  editingName={editingName}
                  tempName={tempName}
                  setTempName={setTempName}
                  onSaveName={handleSaveName}
                  onStartEditName={handleStartEditName}
                  onSetDefault={handleSetDefault}
                  onOpenEditor={handleOpenEditor}
                  onRemovePersonImage={handleRemovePersonImage}
                  onRemoveBackgroundClick={handleRemoveBackgroundClick}
                  onToggleBackground={handleToggleBackground}
                  isOperationInProgress={isOperationInProgress}
                  hasCredits={hasCredits}
                  creditBalance={creditBalance}
                  daysUntilReset={daysUntilReset}
                />
              </>
            )}

            {/* Person Image History */}
            <ImageHistoryGrid
              personImages={personImages}
              activePersonImage={activePersonImage}
              activePersonImageId={activePersonImageId}
              isLoadingImages={isLoadingImages}
              isCanvasIntegrated={isCanvasIntegrated}
              onSelectPersonImage={handleSelectPersonImage}
              onDeleteClick={handleDeleteClick}
              onBulkDelete={handleBulkDeleteClick}
            />
          </div>
        </SusuModal.Content>
      </SusuModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        imageToDelete={imageToDelete}
        isDeleting={isOperationInProgress}
        deleteError={deleteError}
      />

      {/* VTO Deletion Warning Modal (Single) */}
      <DeletionWarningModal
        isOpen={showDeletionWarning}
        onClose={handleCancelDeletionWarning}
        onConfirm={handleConfirmCascadeDelete}
        itemType="person-image"
        vtoCount={vtoCount}
        isDeleting={isDeletingWithCascade}
        error={deletionError}
      />

      {/* Bulk Delete Confirmation Modal (No VTO) */}
      <ConfirmModal
        isOpen={showBulkDeleteConfirm}
        onClose={handleCancelBulkDelete}
        onConfirm={!bulkDeletionError ? handleConfirmBulkDelete : undefined}
        title={`Delete ${bulkDeleteIds.length} Images`}
        titleIconName="trash"
        titleIconColor="red"
        confirmText="Delete All"
        cancelText={bulkDeletionError ? 'Close' : 'Cancel'}
        variant="danger"
        isLoading={isBulkDeleting}
        loadingText="Deleting..."
        confirmDisabled={isBulkDeleting}
        closeOnOverlay={!isBulkDeleting}
      >
        {bulkDeletionError ? (
          <div className="confirm-modal-error">
            <Icon name="exclamation triangle" style={{ margin: 0 }} />
            <span>{bulkDeletionError}</span>
          </div>
        ) : (
          <p>Are you sure you want to delete {bulkDeleteIds.length} person image{bulkDeleteIds.length > 1 ? 's' : ''}?</p>
        )}
      </ConfirmModal>

      {/* Bulk VTO Deletion Warning Modal */}
      <ConfirmModal
        isOpen={showBulkDeletionWarning}
        onClose={!isBulkDeleting ? handleCancelBulkDelete : undefined}
        onConfirm={handleConfirmBulkCascadeDelete}
        title={`Delete ${bulkDeleteIds.length} person images?`}
        titleIconName="warning sign"
        titleIconColor="red"
        confirmText="Delete All"
        cancelText="Cancel"
        variant="danger"
        isLoading={isBulkDeleting}
        loadingText="Deleting..."
        confirmDisabled={isBulkDeleting}
        closeOnOverlay={!isBulkDeleting}
      >
        <p>
          These images are associated with <strong style={{ color: '#dc2626' }}>{bulkVtoCount} virtual try-on result{bulkVtoCount !== 1 ? 's' : ''}</strong>.
        </p>
        <div className="confirm-modal-warning">
          <Icon name="warning sign" color="orange" style={{ margin: 0, flexShrink: 0 }} />
          <span>
            If you delete these images, all associated virtual try-on results will also be permanently deleted.
          </span>
        </div>
        <p style={{ fontWeight: 500, marginTop: 16 }}>
          Are you sure you want to continue?
        </p>
        {bulkDeletionError && (
          <div className="confirm-modal-error">
            <Icon name="warning circle" style={{ margin: 0 }} />
            <span><strong>Deletion Failed:</strong> {bulkDeletionError}</span>
          </div>
        )}
      </ConfirmModal>

      {/* Image Editor Modal */}
      <SusuModal
        open={isEditorOpen}
        onClose={() => !isSavingCrop && setIsEditorOpen(false)}
        size="large"
        className="image-editor-modal"
        closeIcon
      >
        <SusuModal.Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="crop" style={{ margin: 0 }} />
            <span>Crop & Resize Image</span>
          </div>
        </SusuModal.Header>
        <SusuModal.Content scrolling>
          {editorImageUrl && (
            <PersonImageEditor
              imageUrl={editorImageUrl}
              onSave={handleEditorSave}
              onCancel={() => setIsEditorOpen(false)}
              isSaving={isSavingCrop}
            />
          )}
        </SusuModal.Content>
      </SusuModal>

      {/* Background Removal Processing Modal */}
      <ProcessingModal
        isOpen={showBgRemovalProcessing}
        title="Background Removal"
        message="Removing background from your image..."
        icon="image"
        creditBalance={creditBalance}
        creditsUsed={1}
        tip="Creating a clean cutout..."
      />
    </>
  );
};

export default PersonImageModal;